/**
 * CurrentMonthTimesheetCard (Phase 41 Plan 41-05 Task 1).
 *
 * Re-skinned extraction of the v6-era dashboard's CurrentTimesheetCard (that
 * dashboard page was retired in Phase 89). Same data path
 * (`getCurrentTimesheet`), v7 Card chrome, English-only copy.
 *
 * Quick task 260512-uat-batch (F-04, 2026-05-12) — card is monthly (title
 * is `getCurrentTimesheet().period.name`, hours are monthly totals) but
 * the subtitle and progress bar target said "/ 40h this week". Switched
 * to a monthly target derived from business days × 8h so the percentage
 * actually reflects monthly progress.
 *
 * Quick task 260929-bq9 (MKS-179, 2026-09-29) — the business-days-only
 * target above was holiday-blind and disagreed with the Timesheets page's
 * holiday-aware value. Replaced with the shared `getMonthlyTargetHours`
 * utility (same algorithm as `TimeEntryPage.tsx`), fed by a holidays query
 * for the current year.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Btn } from '../ui/Btn';
import { getCurrentTimesheet, getHolidays } from '../../services/api/timeEntry';
import { getMonthlyTargetHours } from '../../utils/workingHours';

export function CurrentMonthTimesheetCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'currentTimesheet'],
    queryFn: getCurrentTimesheet,
  });
  const ts = data?.data;
  const hours = ts?.totalHours ?? 0;
  const title = ts?.period?.name ?? 'Current Timesheet';

  const now = new Date();
  const year = now.getFullYear();

  // Fetch holidays for the year (mirrors TimeEntryPage.tsx's pattern).
  const { data: holidaysData } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => getHolidays(year),
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });

  const holidayDates = new Set<string>();
  if (holidaysData?.data) {
    for (const holiday of holidaysData.data) {
      holidayDates.add(holiday.date);
    }
  }

  const monthlyTarget = getMonthlyTargetHours(year, now.getMonth(), holidayDates);
  const pct = monthlyTarget > 0 ? Math.min(100, Math.round((hours / monthlyTarget) * 100)) : 0;

  return (
    <Card title={title}>
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-[26px] font-bold tabular-nums text-ms-text-strong">
              {hours.toFixed(1)}
            </div>
            <div className="text-[12px] text-ms-text-muted">/ {monthlyTarget}h this month</div>
          </div>
          <div className="h-1.5 rounded-full bg-ms-surface-2 overflow-hidden mb-4">
            <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex gap-2">
            <Link to="/time" className="flex-1">
              <Btn variant="primary" size="sm" className="w-full">
                Log hours
              </Btn>
            </Link>
            <Link to="/time">
              <Btn variant="secondary" size="sm">
                Submit
              </Btn>
            </Link>
          </div>
        </>
      )}
    </Card>
  );
}
